import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import {
  readStoredMentorWorkspace,
  resolveMentorWorkspace,
  type MentorWorkspaceId,
} from "./notice/workspace";

export type MentorWorkflowSegment = MentorWorkspaceId;

const CONVERSATIONS_ROUTE = "/conversations/" as const;

interface MentorWorkflowsSearch {
  segment?: string;
  channel?: string;
  internalChannelId?: string;
}

export interface MentorWorkflowsSelection {
  segment: MentorWorkflowSegment;
  channel: string | null;
  internalChannelId: string | null;
  selectedConversation: {
    channel: string;
    internalChannelId: string | null;
  } | null;
  selectChannel: (
    channelId: string,
    internalChannelId?: string | null,
  ) => void;
}

function normalizeSearchValue(value: unknown) {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function useMentorWorkflowsSelection(): MentorWorkflowsSelection {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as MentorWorkflowsSearch;
  const [storedWorkspace] = useState<MentorWorkspaceId | null>(() =>
    readStoredMentorWorkspace(),
  );

  const segment = resolveMentorWorkspace(search.segment, storedWorkspace);
  const channel = normalizeSearchValue(search.channel);
  const internalChannelId = normalizeSearchValue(search.internalChannelId);

  const selectChannel = useCallback(
    (channelId: string, selectedInternalChannelId?: string | null) => {
      void navigate({
        from: CONVERSATIONS_ROUTE,
        search: (prev) => ({
          ...prev,
          segment,
          channel: channelId,
          internalChannelId: selectedInternalChannelId ?? undefined,
        }),
        replace: true,
      });
    },
    [navigate, segment],
  );

  return {
    segment,
    channel,
    internalChannelId,
    selectedConversation: channel
      ? {
          channel,
          internalChannelId,
        }
      : null,
    selectChannel,
  };
}
